import React, { useEffect, useMemo, useRef, useState } from 'react';
import PortalPreview from '../composer/PortalPreview';
import { chipToWindow, customLabel } from '../composer/windowChips';
import SquareSwitch from '../composer/SquareSwitch';
import { MAX_CLIP_MS } from '../composer/StepTrim';
import SendingTakeover from '../composer/SendingTakeover';
import CoverFocusEditor from '../composer/CoverFocusEditor';
import CityPicker from '../../CityPicker';
import VenuePicker from '../../VenuePicker';
import EventLinkPicker from '../../EventLinkPicker';
import PortalCard from './PortalCard';
import PortalMediaFlow from './PortalMediaFlow';
import { Btn, SectionLabel, SquareAvatar, XIcon, RefreshIcon, MAGENTA } from './PortalPageChrome';
import { roomsOf, roomLabel } from './portalPageModel';
import { signalService, friendService } from '../../../services';
import storyService from '../../../services/storyService';
import { usePortalPosting } from '../../../context/PortalPostingContext';
import { FRAME_DEFAULT, clampFrame, frameForPublish, isDefaultFrame } from '../../../utils/mediaFrame';
import { realPlace } from '../../../utils/placeLabel';
import { normalizeLayout, layoutForPublish, composerChip, TEXT_MAX as WORDS_MAX } from '../../../utils/portalLayout';
import { eventBits } from '../../../utils/portalEvent';

/**
 * EDIT IN VIEWER — the host edits their Portal in place, on the viewer's own card, instead of
 * the white form that used to live at /portals/:id/edit.
 *
 * Left: the story card, live — the composer v3 canvas: the words are typed on the Portal (or in
 * the panel's textarea, the two stay in step), the event and the venue are dragged, pinched and
 * styled on the card, the picture pans and zooms under a finger, and REPLACE CLIP opens the
 * composer's PICK + TRIM + COVER over it. Right: the settings panel — WHO SEES IT · WHO CAN
 * JOIN · WINDOW · WHERE · LINK AN EVENT.
 *
 * Nothing leaves the device until SAVE CHANGES. Then the words and settings go up in one PUT,
 * a new file goes up behind the takeover the way a fresh Portal does (the old clip stays live
 * until the new one has fully arrived), and a re-framed existing clip is one small PUT.
 */

const ROOM_OPTIONS = [
  { key: 'public', label: (city) => (city ? `YOUR CITY · ${city.toUpperCase()}` : 'YOUR CITY'), sub: 'The whole town, granted by geography', span: '1 / -1' },
  { key: 'friends', label: () => 'FRIENDS', sub: 'People you added' },
  { key: 'close_friends', label: () => 'CLOSE FRIENDS', sub: 'Your inner circle' },
  { key: 'select', label: () => 'SELECT PEOPLE', sub: 'Pick who sees it' },
  { key: 'club', label: (_c, n) => 'CLUBS', sub: (n) => (n ? `${n} ${n === 1 ? 'club' : 'clubs'} · members only` : 'Members only') },
];

const label11 = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em' };

function Section({ title, children }) {
  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      <SectionLabel>{title}</SectionLabel>
      {children}
    </div>
  );
}

/** Build the editor's draft from the card the page already holds. */
export function draftFrom(signal) {
  const city = [signal.primaryCityLabel, signal.locationLabel].map((c) => realPlace(c)).find(Boolean) || '';
  return {
    text: signal.signalText || '',
    layout: normalizeLayout(signal.layout),
    rooms: roomsOf(signal),
    clubIds: Array.isArray(signal.clubIds) ? signal.clubIds : [],
    selectedUserIds: Array.isArray(signal.selectedUserIds) ? signal.selectedUserIds : [],
    mode: signal.mode === 'curated' ? 'curated' : 'open',
    windowOn: true,
    windowStart: signal.dateTimeUtc ? { label: customLabel(signal.dateTimeUtc), iso: signal.dateTimeUtc } : chipToWindow('NOW'),
    windowEnd: signal.expiresAtUtc ? { label: customLabel(signal.expiresAtUtc), iso: signal.expiresAtUtc } : chipToWindow('11:00 PM'),
    city: city || signal.latitude != null ? { slug: null, label: city, lat: signal.latitude ?? null, lng: signal.longitude ?? null } : null,
    venue: signal.venueName ? { name: signal.venueName, address: signal.venueAddress || '', lat: signal.latitude ?? null, lng: signal.longitude ?? null } : null,
    venueOpen: !!signal.venueOpen,
    event: signal.sourceEvent ? {
      id: signal.sourceEvent.eventId,
      title: signal.sourceEvent.title,
      imageUrl: signal.sourceEvent.posterUrl || null,
      startsAtUtc: signal.sourceEvent.dateTimeUtc || null,
      meta: signal.sourceEvent.locationCity || '',
    } : null,
  };
}

/** The PUT body for the words and settings. `textOnly`: no picture, so the background counts. */
export function requestFrom(draft, { textOnly = true } = {}) {
  const rooms = draft.rooms.filter((r) => r !== 'club');
  const audiences = [...rooms];
  if (draft.clubIds.length) audiences.push('club');
  return {
    signalText: draft.text.trim(),
    layout: layoutForPublish(draft.layout, { textOnly, hasVenue: !!draft.venue, hasEvent: !!draft.event }),
    audience: audiences[0] || 'friends',
    audiences,
    clubIds: draft.clubIds.length ? draft.clubIds : null,
    selectedUserIds: rooms.includes('select') ? draft.selectedUserIds : null,
    radius: 'local',
    mode: draft.mode,
    sourceEventId: draft.event?.id || null,
    startUtc: draft.windowOn ? draft.windowStart.iso : null,
    endUtc: draft.windowOn ? draft.windowEnd.iso : null,
    latitude: draft.venue?.lat ?? draft.city?.lat ?? null,
    longitude: draft.venue?.lng ?? draft.city?.lng ?? null,
    locationLabel: draft.city?.label || null,
    venueName: draft.venue?.name || null,
    venueAddress: draft.venue?.address || null,
    venueOpen: !!draft.venue && !!draft.venueOpen,
  };
}

export default function PortalEditor({ signal, media, mobile = false, onClose, onSaved }) {
  const { startPosting } = usePortalPosting();
  const [draft, setDraft] = useState(() => draftFrom(signal));
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  // The picture: either the clip that's up (re-framable in place) or a new local file.
  const [mediaDraft, setMediaDraft] = useState(null);          // null = keep what's up
  const [existingFrame, setExistingFrame] = useState(() => clampFrame(media?.frame || FRAME_DEFAULT));
  const [existingFocus, setExistingFocus] = useState(() => media?.coverFocus || null);
  const [newFrame, setNewFrame] = useState(FRAME_DEFAULT);
  const [flowOpen, setFlowOpen] = useState(false);
  const [focusOpen, setFocusOpen] = useState(false);
  const [maxClipMs, setMaxClipMs] = useState(MAX_CLIP_MS);
  const [saving, setSaving] = useState(false);
  const [takingOver, setTakingOver] = useState(false);
  const [error, setError] = useState('');

  const [friends, setFriends] = useState([]);
  const [friendSearch, setFriendSearch] = useState('');
  const [clubs, setClubs] = useState([]);
  const [whereOpen, setWhereOpen] = useState(false);
  const [picking, setPicking] = useState(null);   // 'start' | 'end' | null
  // The card's own editing state: typing on the words, or an event / venue with its style bar.
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(null);
  const localUrlRef = useRef(null);
  const handedToUpload = useRef(false);

  useEffect(() => {
    let done = false;
    storyService.getCapabilities?.()?.then?.((r) => { const s = r?.data?.maxDurationSec; if (!done && s > 0) setMaxClipMs(s * 1000); });
    friendService?.getFriends?.()?.then?.((r) => { if (!done && r?.success) setFriends((r.data || []).filter((f) => f.userId)); });
    import('../../../services/clubService')
      .then(({ default: cs }) => cs.getMyClubs())
      .then((r) => { if (!done && r?.success) setClubs((r.data?.items || r.data || []).filter((c) => c?.id)); })
      .catch(() => {});
    return () => { done = true; };
  }, []);

  // A file we picked and then didn't send is ours to let go of.
  useEffect(() => () => {
    if (handedToUpload.current) return;
    if (localUrlRef.current) { try { URL.revokeObjectURL(localUrlRef.current); } catch { /* gone */ } }
  }, []);

  const hasNewFile = !!(mediaDraft && mediaDraft.media?.file);
  const dropsMedia = !!(mediaDraft && mediaDraft.media?.kind === 'text');
  const cardMedia = dropsMedia ? null : hasNewFile ? null : media;
  const previewMedia = hasNewFile ? mediaDraft.media : null;

  const chip = roomLabel(draft.rooms.concat(draft.clubIds.length && !draft.rooms.includes('club') ? ['club'] : []));
  const n = draft.text.length;
  const timeChip = composerChip(draft.windowOn, draft.windowStart.iso, draft.windowEnd.iso);
  const venueOnCard = draft.venue ? { name: draft.venue.name, address: draft.venue.address || draft.city?.label || '' } : null;
  const textOnly = dropsMedia || (!hasNewFile && !media);

  const toggleRoom = (key) => {
    const has = draft.rooms.includes(key);
    set({ rooms: has ? draft.rooms.filter((r) => r !== key) : [...draft.rooms, key] });
  };
  const toggleClub = (id) => set({ clubIds: draft.clubIds.includes(id) ? draft.clubIds.filter((x) => x !== id) : [...draft.clubIds, id] });
  const togglePerson = (id) => set({ selectedUserIds: draft.selectedUserIds.includes(id) ? draft.selectedUserIds.filter((x) => x !== id) : [...draft.selectedUserIds, id] });

  const filteredFriends = useMemo(() => {
    const q = friendSearch.trim().toLowerCase();
    return q ? friends.filter((f) => [f.displayName, f.handle, f.email].some((v) => (v || '').toLowerCase().includes(q))) : friends;
  }, [friends, friendSearch]);

  const anyRoom = draft.rooms.some((r) => r !== 'club') || draft.clubIds.length > 0;
  const canSave = (draft.text.trim().length > 0 || !textOnly) && anyRoom && (!draft.rooms.includes('select') || draft.selectedUserIds.length > 0) && !saving;

  const onFlowDone = (result) => {
    setFlowOpen(false);
    if (result.media?.localUrl && result.media.localUrl !== localUrlRef.current) {
      if (localUrlRef.current) { try { URL.revokeObjectURL(localUrlRef.current); } catch { /* gone */ } }
      localUrlRef.current = result.media.localUrl;
    }
    setMediaDraft(result);
    setNewFrame(FRAME_DEFAULT);
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    const r = await signalService.update(signal.eventId, requestFrom(draft, { textOnly }));
    if (!r.success) {
      setSaving(false);
      setError(r.error || 'Something broke on our end. Try that again?');
      return;
    }
    let mediaResult = { media, changed: false };

    if (dropsMedia && media?.id) {
      await storyService.remove(media.id);
      mediaResult = { media: null, changed: true };
    } else if (hasNewFile) {
      const { media: m, trim, coverPct, coverFocus, cover } = mediaDraft;
      const clipMs = trim ? trim.endMs - trim.startMs : 0;
      handedToUpload.current = true;
      // Not awaited: the upload runs behind the takeover and the toast reports on it.
      startPosting({
        eventId: signal.eventId,
        replaceHero: true,
        media: { ...m, clip: m.kind === 'video' && trim ? trim : null },
        signalRequest: {
          audience: draft.rooms.find((x) => x !== 'club') || 'friends',
          sourceEventId: draft.event?.id || null,
          latitude: draft.venue?.lat ?? draft.city?.lat ?? null,
          longitude: draft.venue?.lng ?? draft.city?.lng ?? null,
        },
        coverMs: m.kind === 'video' && trim ? Math.round(trim.startMs + (coverPct ?? 0.3) * clipMs) : null,
        coverFocus: m.kind === 'video' ? coverFocus : null,
        coverUrl: cover || null,
        frame: frameForPublish(newFrame),
        trim,
      });
      mediaResult = { media: null, changed: true, uploading: true };
      setTakingOver(true);
    } else if (media?.id) {
      const frameChanged = JSON.stringify(clampFrame(media.frame || FRAME_DEFAULT)) !== JSON.stringify(existingFrame);
      const focusChanged = JSON.stringify(media.coverFocus || null) !== JSON.stringify(existingFocus || null);
      if (frameChanged || focusChanged) {
        const u = await storyService.update(media.id, {
          ...(frameChanged ? (isDefaultFrame(existingFrame) ? { resetFrame: true } : { frame: frameForPublish(existingFrame) }) : {}),
          ...(focusChanged && existingFocus ? { coverFocus: { x: Math.round(existingFocus.x), y: Math.round(existingFocus.y) } } : {}),
        });
        mediaResult = { media: u.success ? u.data : media, changed: true };
      }
    }
    setSaving(false);
    if (!hasNewFile) onSaved?.(r.data, mediaResult);
    else onSaved?.(r.data, mediaResult, { deferClose: true });
  };

  // ---- the card ----
  const replaceLabel = (previewMedia || media)?.kind === 'image' || previewMedia?.kind === 'photo' ? 'REPLACE PHOTO' : (previewMedia || media) ? 'REPLACE CLIP' : 'ADD A CLIP OR PHOTO';
  const replaceBtn = (
    <div className="absolute flex" style={{ top: 56, left: 16, right: 16, gap: 8, zIndex: 6 }}>
      <Btn kind="black" size="sm" onClick={() => setFlowOpen(true)} className="flex items-center" style={{ gap: 8, letterSpacing: '0.12em' }} data-testid="replace-media">
        <RefreshIcon /> {replaceLabel}
      </Btn>
      {media?.kind === 'video' && !previewMedia && !dropsMedia && media.thumbnailUrl && (
        <Btn kind="black" size="sm" onClick={() => setFocusOpen(true)} style={{ letterSpacing: '0.12em' }}>AIM THE RING</Btn>
      )}
    </div>
  );

  const setLayout = (l) => set({ layout: l });
  const eventOnCard = draft.event ? eventBits(draft.event) : null;
  const editorOptions = {
    onText: (t) => set({ text: t }),
    editing, onEditing: setEditing,
    selected, onSelected: setSelected,
    // The panel links and unlinks; the card's rail keeps the words and the reset.
    rail: ['text', 'reset'],
    onRemove: (k) => set(k === 'event' ? { event: null } : { venue: null }),
    showChrome: true,
    venueOpen: !!draft.venueOpen,
    onVenueOpen: (v) => set({ venueOpen: v }),
    mode: draft.mode,
  };

  const card = previewMedia ? (
    // A new local file plays from the device; the words and the venue are dragged over it.
    <div className="relative w-full overflow-hidden" style={{ aspectRatio: '9 / 16', background: '#000', border: '2px solid #fff' }} data-testid="portal-card">
      <PortalPreview
        media={previewMedia}
        text={draft.text}
        chip={timeChip}
        layout={draft.layout}
        onLayout={setLayout}
        venue={venueOnCard}
        event={eventOnCard}
        editor={{ ...editorOptions, frame: newFrame, onFrame: (f) => setNewFrame(clampFrame(f)), isVideo: previewMedia.kind === 'video' }}
        clip={previewMedia.kind === 'video' && mediaDraft.trim ? mediaDraft.trim : null}
        frame={newFrame}
        showHeader={false}
        className="absolute inset-0"
      />
      {replaceBtn}
    </div>
  ) : (
    <PortalCard
      signal={{ ...signal, mode: draft.mode, layout: draft.layout }}
      text={draft.text}
      chip={timeChip}
      venue={venueOnCard}
      event={eventOnCard}
      media={cardMedia}
      frame={cardMedia ? existingFrame : undefined}
      roomLabel={chip}
      showHeader={false}
      layoutEditor={{ onLayout: setLayout, textOnly, ...editorOptions, frame: cardMedia ? existingFrame : null, onFrame: cardMedia ? (f) => setExistingFrame(clampFrame(f)) : null, isVideo: cardMedia?.kind === 'video' }}
    >
      {replaceBtn}
    </PortalCard>
  );

  const roomSub = (opt) => (typeof opt.sub === 'function' ? opt.sub(clubs.length) : opt.sub);

  return (
    <div className="pp-page fixed inset-0 safe-inset z-[95] overflow-auto font-mono text-white" style={{ background: '#1A1A1A' }} role="dialog" aria-modal="true" aria-label="Edit your portal" data-testid="portal-editor">
      <div className="sticky top-0 z-[2] flex items-center justify-between" style={{ gap: 16, padding: mobile ? '14px 16px' : '20px 32px', background: '#1A1A1A' }}>
        <div className="flex items-center" style={{ gap: 12 }}>
          <SquareAvatar url={signal.hostAvatarUrl} name={signal.hostDisplayName} size={34} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Editing your portal</div>
            <div className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: MAGENTA }}>Changes show to everyone who can see it</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="pr-viewer-close flex items-center justify-center"
          style={{ width: 40, height: 40, border: '2px solid rgba(255,255,255,.4)', color: '#fff', transition: 'background-color .2s, color .2s' }}
        >
          <XIcon />
        </button>
      </div>

      <div className="flex flex-wrap items-start justify-center" style={{ gap: mobile ? 16 : 40, padding: mobile ? '0 16px 32px' : '8px 32px 48px' }}>
        <div className="flex flex-col" style={{ flex: mobile ? '1 1 100%' : '0 1 400px', minWidth: 260, gap: 10 }}>
          {card}
        </div>

        <div className="flex min-w-0 flex-col" style={{ flex: '1 1 360px', maxWidth: 440, gap: 22, border: '2px solid #fff', background: '#000', padding: 22 }}>
          <Section title="The words">
            <textarea
              value={draft.text}
              onChange={(e) => set({ text: e.target.value.slice(0, WORDS_MAX) })}
              maxLength={WORDS_MAX}
              rows={3}
              aria-label="Portal text"
              data-testid="editor-words"
              className="block w-full resize-none bg-transparent text-white"
              style={{ padding: '10px 12px', border: '2px solid #fff', fontSize: 15, lineHeight: 1.4, boxSizing: 'border-box' }}
            />
            <div className="flex justify-between" style={{ fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,.6)' }}>
              <span>OR TAP THE WORDS ON THE CARD · DRAG, PINCH, TWIST</span>
              <span style={{ color: n >= 130 ? '#EAB308' : undefined }}>{n}/{WORDS_MAX}</span>
            </div>
          </Section>

          <Section title="Who sees it">
            <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
              {ROOM_OPTIONS.map((opt) => {
                const on = opt.key === 'club' ? draft.rooms.includes('club') || draft.clubIds.length > 0 : draft.rooms.includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleRoom(opt.key)}
                    className="text-left font-mono"
                    style={{ padding: '10px 12px', background: on ? '#fff' : 'transparent', color: on ? '#000' : '#fff', border: '2px solid #fff', gridColumn: opt.span || 'auto', transition: 'background-color .2s, color .2s', cursor: 'pointer' }}
                  >
                    <span className="block" style={label11}>{opt.label(draft.city?.label, clubs.length)}</span>
                    <span className="block" style={{ marginTop: 3, fontSize: 10, letterSpacing: '0.06em', opacity: 0.7 }}>{roomSub(opt)}</span>
                  </button>
                );
              })}
            </div>

            {draft.rooms.includes('select') && (
              <div style={{ border: '2px solid rgba(255,255,255,.5)', padding: 10 }} data-testid="people-picker">
                <input
                  value={friendSearch}
                  onChange={(e) => setFriendSearch(e.target.value)}
                  placeholder="Search your friends…"
                  className="w-full bg-transparent text-white"
                  style={{ border: '2px solid rgba(255,255,255,.5)', padding: '8px 10px', fontSize: 12, boxSizing: 'border-box' }}
                />
                <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10, letterSpacing: '0.1em', color: 'rgba(255,255,255,.6)' }}>
                  <span>{draft.selectedUserIds.length} PICKED</span>
                  {draft.selectedUserIds.length > 0 && <button type="button" onClick={() => set({ selectedUserIds: [] })} className="underline">CLEAR</button>}
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8 }}>
                  {filteredFriends.length === 0 && <p style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', padding: '6px 0' }}>{friends.length ? 'No one by that name.' : 'Add some friends first.'}</p>}
                  {filteredFriends.map((f) => {
                    const on = draft.selectedUserIds.includes(f.userId);
                    return (
                      <button key={f.userId} type="button" role="checkbox" aria-checked={on} onClick={() => togglePerson(f.userId)} className="flex w-full items-center text-left" style={{ gap: 10, padding: '6px 0' }}>
                        <SquareAvatar url={f.avatarUrl} name={f.displayName} size={28} />
                        <span className="min-w-0 flex-1 truncate" style={{ fontSize: 12, fontWeight: 700 }}>{f.displayName}<span style={{ marginLeft: 6, fontWeight: 400, opacity: 0.6 }}>{f.handle ? `@${f.handle}` : ''}</span></span>
                        <span className="flex flex-shrink-0 items-center justify-center" style={{ width: 18, height: 18, border: '2px solid #fff' }} aria-hidden>{on && <span style={{ width: 8, height: 8, background: '#fff' }} />}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {(draft.rooms.includes('club') || draft.clubIds.length > 0) && (
              <div className="flex flex-col" style={{ gap: 6 }} data-testid="club-picker">
                {clubs.length === 0 && <p style={{ fontSize: 11, color: 'rgba(255,255,255,.5)' }}>You’re not in any clubs yet.</p>}
                {clubs.map((c) => {
                  const on = draft.clubIds.includes(c.id);
                  return (
                    <button key={c.id} type="button" role="checkbox" aria-checked={on} onClick={() => toggleClub(c.id)} className="flex w-full items-center justify-between text-left" style={{ gap: 10, padding: '8px 10px', border: `2px solid ${on ? '#fff' : 'rgba(255,255,255,.4)'}` }}>
                      <span className="min-w-0 truncate uppercase" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>{c.name}</span>
                      <span className="flex flex-shrink-0 items-center justify-center" style={{ width: 18, height: 18, border: '2px solid #fff' }} aria-hidden>{on && <span style={{ width: 8, height: 8, background: '#fff' }} />}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </Section>

          <Section title="Who can join">
            <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', border: '2px solid #fff' }}>
              {[['open', 'OPEN', 'Everyone is welcome'], ['curated', 'CURATED', 'They ask, you pick']].map(([k, l, s]) => {
                const on = draft.mode === k;
                return (
                  <button key={k} type="button" aria-pressed={on} onClick={() => set({ mode: k })} className="text-left font-mono" style={{ padding: '10px 12px', background: on ? '#fff' : 'transparent', color: on ? '#000' : '#fff', border: 0, transition: 'background-color .2s, color .2s', cursor: 'pointer' }}>
                    <span className="block" style={label11}>{l}</span>
                    <span className="block" style={{ marginTop: 3, fontSize: 10, opacity: 0.7 }}>{s}</span>
                  </button>
                );
              })}
            </div>
          </Section>

          <Section title="Window">
            <div className="flex items-center justify-between" style={{ gap: 12 }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,.7)' }}>{draft.windowOn ? 'Runs between these two moments.' : 'Live now, gone in four hours.'}</span>
              <SquareSwitch on={draft.windowOn} onChange={(v) => set({ windowOn: v })} label="Give it a window" />
            </div>
            {draft.windowOn && (
              <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
                {[['start', 'STARTS', draft.windowStart], ['end', 'ENDS', draft.windowEnd]].map(([k, l, v]) => (
                  <button key={k} type="button" onClick={() => setPicking((p) => (p === k ? null : k))} className="text-left font-mono text-white" style={{ padding: '10px 12px', border: `2px solid ${picking === k ? '#00FFFF' : '#fff'}`, background: 'transparent', cursor: 'pointer' }}>
                    <span className="block" style={{ fontSize: 10, letterSpacing: '0.1em', opacity: 0.7 }}>{l}</span>
                    <span className="block" style={{ marginTop: 3, fontSize: 12, fontWeight: 700 }}>{v.label}</span>
                  </button>
                ))}
              </div>
            )}
            {draft.windowOn && picking && (
              <input
                type="datetime-local"
                aria-label={picking === 'start' ? 'Starts' : 'Ends'}
                value={toLocalInput((picking === 'start' ? draft.windowStart : draft.windowEnd).iso)}
                onChange={(e) => {
                  const dt = new Date(e.target.value);
                  if (Number.isNaN(dt.getTime())) return;
                  const val = { label: customLabel(dt.toISOString()), iso: dt.toISOString() };
                  set(picking === 'start' ? { windowStart: val } : { windowEnd: val });
                }}
                className="w-full bg-black text-white"
                style={{ border: '2px solid #00FFFF', padding: '12px 14px', fontSize: 14, colorScheme: 'dark', boxSizing: 'border-box' }}
              />
            )}
          </Section>

          <Section title={draft.venueOpen ? 'Where · shown to everyone' : 'Where · shown once they’re in'}>
            {draft.venue && (
              <div className="flex items-center justify-between" style={{ gap: 12 }} data-testid="editor-venue-open">
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.7)' }}>
                  <span className="block font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.14em', color: '#fff' }}>Show the place right away</span>
                  {draft.venueOpen ? 'Everyone who can see the Portal sees the place and address.' : draft.mode === 'curated' ? 'Off: the place stays hidden until you let them in.' : 'Off: the place stays hidden until they tap I’M DOWN.'}
                </span>
                <SquareSwitch on={!!draft.venueOpen} onChange={(v) => set({ venueOpen: v })} label="Show the place right away" />
              </div>
            )}
            {!whereOpen ? (
              <div className="flex items-center" style={{ gap: 12, padding: '10px 12px', border: '2px solid #fff' }}>
                <div className="min-w-0 flex-1">
                  <div className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{draft.venue ? `@ ${draft.venue.name}` : (draft.city?.label || 'Not set')}</div>
                  <div className="truncate" style={{ marginTop: 2, fontSize: 11, opacity: 0.7 }}>{draft.venue ? (draft.venue.address || draft.city?.label || '') : (draft.city ? 'No venue picked' : 'Pick a city, then a place in it')}</div>
                </div>
                <button type="button" onClick={() => setWhereOpen(true)} className="font-mono font-bold uppercase underline" style={{ fontSize: 10, letterSpacing: '0.12em' }}>{draft.venue || draft.city ? 'Change' : 'Add'}</button>
              </div>
            ) : (
              <div className="flex flex-col" style={{ gap: 12 }}>
                <CityPicker value={draft.city} onChange={(c) => set({ city: c, venue: null })} />
                <VenuePicker value={draft.venue} onChange={(v) => set({ venue: v })} cityCoords={draft.city ? { lat: draft.city.lat, lng: draft.city.lng } : null} cityLabel={draft.city?.label || ''} />
                <button type="button" onClick={() => setWhereOpen(false)} className="self-start font-mono font-bold uppercase underline" style={{ fontSize: 10, letterSpacing: '0.12em' }}>Done</button>
              </div>
            )}
          </Section>

          <Section title="Link an event · optional">
            <EventLinkPicker value={draft.event} onChange={(ev) => set({ event: ev })} />
          </Section>

          {error && <p style={{ fontSize: 12, color: '#FF2D2D' }}>{error}</p>}

          <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10, paddingTop: 6 }}>
            <Btn kind="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
            <Btn kind="primary" onClick={save} disabled={!canSave} data-testid="save-changes">{saving ? 'Saving…' : 'Save changes'}</Btn>
          </div>
        </div>
      </div>

      {flowOpen && (
        <PortalMediaFlow
          initial={mediaDraft}
          maxClipMs={maxClipMs}
          desktop={!mobile}
          onDone={onFlowDone}
          onCancel={() => setFlowOpen(false)}
        />
      )}

      {focusOpen && media?.thumbnailUrl && (
        <CoverFocusEditor
          cover={media.thumbnailUrl}
          focus={existingFocus || { x: 50, y: 50 }}
          onChange={setExistingFocus}
          onClose={() => setFocusOpen(false)}
        />
      )}

      {takingOver && (
        <SendingTakeover
          coverUrl={mediaDraft?.cover || null}
          avatarUrl={signal.hostAvatarUrl || null}
          roomLabel={chip}
          onDone={onClose}
        />
      )}
    </div>
  );
}

function toLocalInput(iso) {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
